/**
 * Indicador do dashboard: rótulo, valor grande, dica e delta opcional.
 * Delta nunca depende só de cor: leva sinal (▲/▼) e texto.
 */
import { createElement as h, type ReactElement, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cx } from "./cx.js";

export interface StatTileProps {
  label: string;
  value: ReactNode;
  hint?: string;
  icon?: LucideIcon;
  /** Variação vs. período anterior (ex.: 0.12 = +12%). */
  delta?: number;
  /** `true` quando subir é ruim (ex.: cancelamento). */
  invertDelta?: boolean;
  [dataAttr: `data-${string}`]: string | number | boolean | undefined;
}

export function StatTile({ label, value, hint, icon, delta, invertDelta, ...rest }: StatTileProps): ReactElement {
  const good = delta === undefined ? true : invertDelta ? delta <= 0 : delta >= 0;
  return h(
    "div",
    { ...rest, className: "flex flex-col gap-1 rounded-panel border border-border bg-surface p-5 shadow-s1" },
    h(
      "div",
      { className: "flex items-center justify-between gap-2" },
      h("p", { className: "text-sm font-extrabold text-muted" }, label),
      icon
        ? h(
            "span",
            { className: "grid h-[32px] w-[32px] place-items-center rounded-control bg-surface-soft text-ink" },
            h(icon, { className: "h-4 w-4", "aria-hidden": true, focusable: false })
          )
        : null
    ),
    h("p", { className: "mt-1 text-[2rem] font-black leading-none tracking-tight text-ink tabular-nums" }, value),
    delta !== undefined
      ? h(
          "p",
          { className: cx("text-xs font-bold", good ? "text-success" : "text-danger") },
          `${delta >= 0 ? "▲" : "▼"} ${Math.abs(delta * 100).toFixed(1).replace(".", ",")}% vs. período anterior`
        )
      : null,
    hint ? h("p", { className: "mt-1 text-xs text-muted" }, hint) : null
  );
}
